import { useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Shield, Award, Headphones, TrendingUp, Home, Users, MapPin, Bed, Bath, Square } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const ICON_MAP: Record<string, React.ElementType> = {
  Shield, Award, Headphones, TrendingUp, Home, Users,
};

// ─── BROKER HERO ─── Video bg, centered luxury, gold stats bar
export function BrokerHero() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'hero');
  const c = {
    subheading: "Gurgaon's Trusted Property Partner",
    heading: "Swipe Into Your Dream Home.",
    description: "We help you find premium residential & commercial properties in prime locations.",
    buttonText: "Get Free Consultation",
    buttonLink: "/contact",
    buttonText2: "Explore Properties",
    buttonLink2: "/properties",
    videoUrl: "https://videos.pexels.com/video-files/5490898/5490898-uhd_2560_1440_25fps.mp4",
    imageUrl: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80",
    stats: [{ value: "8+", label: "Years Experience" }, { value: "350+", label: "Properties Sold" }, { value: "800+", label: "Happy Families" }],
    ...content,
  };
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        {c.videoUrl ? (
          <video autoPlay muted loop playsInline poster={c.imageUrl} className="w-full h-full object-cover">
            <source src={c.videoUrl} type="video/mp4" />
          </video>
        ) : (
          <img src={c.imageUrl} alt="Hero" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--primary))]/70 via-[hsl(var(--primary))]/60 to-[hsl(var(--primary))]/85" />
      </div>
      <div className="max-w-[1400px] mx-auto relative z-10 pt-20 px-4 sm:px-6">
        <div className="max-w-4xl">
          <motion.span initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="inline-block px-4 py-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full text-accent text-xs font-medium tracking-wider uppercase mb-6">
            {c.subheading}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl md:text-6xl font-semibold tracking-tight leading-tight text-primary-foreground mb-6">
            {c.heading}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-primary-foreground/80 max-w-2xl mb-10">
            {c.description}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4">
            <Link to={c.buttonLink || "/contact"} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-semibold text-sm uppercase tracking-wider hover:bg-accent/90 transition-all">
              {c.buttonText} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={c.buttonLink2 || "/properties"} className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-primary-foreground/30 text-primary-foreground font-semibold text-sm uppercase tracking-wider hover:bg-primary-foreground/10 transition-all">
              <Play className="w-4 h-4" /> {c.buttonText2 || "Explore Properties"}
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-primary-foreground/20">
            {(c.stats || []).map((s: any) => (
              <div key={s.label}><div className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground mb-2">{s.value}</div><div className="text-primary-foreground/60 text-xs uppercase tracking-wider">{s.label}</div></div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── BROKER FEATURED LISTINGS ─── 3-column cards with image hover
export function BrokerFeaturedListings() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'featured-listings');
  const c = {
    subheading: "Exclusive Listings",
    heading: "Featured Properties",
    description: "Handpicked premium properties in prime locations.",
    ...content,
  };

  // Read featured properties from admin localStorage
  const listings = useMemo(() => {
    try {
      const stored = localStorage.getItem('admin_properties_v1');
      if (stored) {
        const all = JSON.parse(stored);
        const featured = all.filter((p: any) => p.featured && p.is_active);
        if (featured.length > 0) return featured.slice(0, 6);
      }
    } catch {}
    return [
      { title: "DLF The Camellias", city: "Golf Course Road", price: 85000000, bedrooms: 4, bathrooms: 5, area_sqft: 6500, primary_image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80", status: "ready_to_move" },
      { title: "Central Park Valley", city: "Sohna Road", price: 18500000, bedrooms: 3, bathrooms: 3, area_sqft: 2450, primary_image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80", status: "newly_launched" },
      { title: "M3M Golf Estate", city: "Sector 65", price: 45000000, bedrooms: 4, bathrooms: 4, area_sqft: 4200, primary_image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80", status: "ready_to_move" },
    ];
  }, []);

  const formatPrice = (price: number) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(1)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(0)} Lac`;
    return `₹${price.toLocaleString()}`;
  };

  const statusLabels: Record<string, string> = {
    available: 'Available', ready_to_move: 'Ready to Move', newly_launched: 'New Launch',
    under_construction: 'Under Construction', pre_launch: 'Pre-Launch', sold: 'Sold',
  };

  return (
    <section className="py-20 bg-secondary">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <span className="text-accent uppercase tracking-widest text-sm font-semibold">{c.subheading}</span>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-4 mb-4">{c.heading}</h2>
          <div className="w-16 h-0.5 bg-accent mx-auto mb-6" />
          {c.description && <p className="text-muted-foreground max-w-xl mx-auto">{c.description}</p>}
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {listings.map((p: any, i: number) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-card rounded-lg overflow-hidden shadow-md group cursor-pointer">
              <Link to={p.slug ? `/properties/${p.slug}` : '#'}>
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img src={p.primary_image || '/placeholder.svg'} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute top-4 left-4"><span className="px-3 py-1 text-xs font-semibold uppercase bg-accent text-accent-foreground rounded">{statusLabels[p.status] || p.status}</span></div>
                  <div className="absolute bottom-4 right-4"><span className="px-4 py-2 bg-card/90 backdrop-blur-sm font-serif text-lg font-semibold rounded">{typeof p.price === 'number' ? formatPrice(p.price) : p.price}</span></div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-accent transition-colors">{p.title}</h3>
                  <div className="flex items-center gap-1 text-muted-foreground text-sm mb-4"><MapPin className="w-4 h-4" />{p.city || p.location}</div>
                  <div className="flex gap-6 pt-4 border-t border-border text-sm text-muted-foreground">
                    {p.bedrooms && <span className="flex items-center gap-1"><Bed className="w-4 h-4" />{p.bedrooms}</span>}
                    {p.bathrooms && <span className="flex items-center gap-1"><Bath className="w-4 h-4" />{p.bathrooms}</span>}
                    {p.area_sqft && <span className="flex items-center gap-1"><Square className="w-4 h-4" />{p.area_sqft} sqft</span>}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BROKER WHY CHOOSE US ─── 6-grid feature cards
export function BrokerWhyChooseUs() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'why-choose-us');
  const c = {
    subheading: "Why Choose Us",
    heading: "The Advantage",
    description: "We go beyond traditional real estate services.",
    items: [
      { title: "RERA Verified", description: "All properties are legally verified for complete peace of mind." },
      { title: "Premium Properties", description: "Curated portfolio of luxury properties in prime locations." },
      { title: "Local Expertise", description: "Deep market knowledge with dedicated relationship managers." },
      { title: "Best Returns", description: "Properties with proven appreciation in high-growth corridors." },
      { title: "End-to-End Service", description: "From property tours to registration — we handle everything." },
      { title: "Exclusive Deals", description: "Access pre-launch offers and off-market properties." },
    ],
    ...content,
  };

  const defaultIcons = [Shield, Award, Headphones, TrendingUp, Home, Users];

  return (
    <section className="py-20 bg-card">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <span className="text-accent uppercase tracking-widest text-sm font-semibold">{c.subheading}</span>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-4 mb-4">{c.heading}</h2>
          <div className="w-16 h-0.5 bg-accent mx-auto" />
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {(c.items || []).map((f: any, i: number) => {
            const Icon = (f.icon && ICON_MAP[f.icon]) || defaultIcons[i % defaultIcons.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="group p-8 rounded-lg bg-secondary hover:bg-primary transition-all duration-500">
                <div className="w-14 h-14 rounded-lg bg-accent/10 group-hover:bg-accent flex items-center justify-center mb-6 transition-all duration-500">
                  <Icon className="w-7 h-7 text-accent group-hover:text-accent-foreground transition-colors duration-500" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3 group-hover:text-primary-foreground transition-colors">{f.title}</h3>
                <p className="text-muted-foreground group-hover:text-primary-foreground/70 text-sm">{f.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
